import { useState, useRef, useCallback } from "react";
import { useKey } from "react-use";
import cn from "app/util/classnames";
import HudButton from "app/ui/HudButton";
import useWindowSize, {
  Aspect,
  type WindowAspectType,
} from "app/util/useWindowSize";

const BUTTON_SELECTED_CLASS = "text-white";
const BUTTON_UNSELECTED_CLASS = "scale-90 text-white";
const LABEL_SELECTED_CLASS = "font-semibold";
const LABEL_UNSELECTED_CLASS = "font-light";
const TRANSITION_CLASSES = "active:scale-100 transition-transform duration-200";
const CLICK_ME_COLOR = "bg-yellow-300";

interface HudRadioButtonProps {
  button0Icon: React.ReactNode;
  button0Label: string;
  button1Icon: React.ReactNode;
  button1Label: string;
  onSelectionChange: (newSelection: number) => void;
  disable: boolean;
  useVerticalLayout?: boolean;
  keyboardShortcut?: string;
  highlightXButton?: boolean;
  highlightColorButton?: boolean;
}

export default function HudRadioButton({
  button0Icon,
  button0Label,
  button1Icon,
  button1Label,
  onSelectionChange,
  useVerticalLayout,
  keyboardShortcut,
  disable,
  highlightXButton,
  highlightColorButton,
}: HudRadioButtonProps) {
  // Integer representing which button is currently selected
  const [selection, setSelection] = useState(0);

  const { aspect } = useWindowSize();

  // A single covering the entire element
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(() => {
    if (!disable) {
      const newSelection = selection === 0 ? 1 : 0;
      setSelection(newSelection);
      onSelectionChange(selection);
    }
  }, [selection, setSelection, disable, onSelectionChange]);

  useKey(
    keyboardShortcut ?? "",
    () => {
      // We don't want space to toggle the tool if the button is already in
      // focus, because browsers already click the focused button when space
      // is pressed.
      if (document.activeElement !== buttonRef.current) {
        handleClick();
      }
    },
    { event: "keyup" },
    [handleClick],
  );

  return (
    <button
      className={cn("flex flex-col", disable && "opacity-40")}
      tabIndex={-1}
      ref={buttonRef}
      onClick={handleClick}
      disabled={disable}
    >
      <div
        className={cn(
          "bg-green-darker rounded-sm",
          "p-0.5",
          "flex gap-0.5",
          useVerticalLayout ? "flex-col" : "flex-row",
        )}
      >
        <ButtonPackage
          isSelected={selection === 0}
          icon={button0Icon}
          labelText={button0Label}
          disable={disable}
          showClickMe={highlightColorButton}
          aspect={aspect}
        />
        <ButtonPackage
          isSelected={selection === 1}
          icon={button1Icon}
          labelText={button1Label}
          disable={disable}
          showClickMe={highlightXButton}
          aspect={aspect}
        />
      </div>
    </button>
  );
}

interface ButtonPackageProps {
  isSelected: boolean;
  icon: React.ReactNode;
  labelText: string;
  disable: boolean;
  onClick?: () => void;
  showClickMe?: boolean;
  aspect: WindowAspectType;
}

function ButtonPackage(props: ButtonPackageProps) {
  return (
    <div className="relative">
      {props.showClickMe && (
        <div
          className={cn(
            "absolute inset-0 animate-mild-ping rounded-sm",
            CLICK_ME_COLOR,
          )}
        />
      )}
      <div
        className={cn(
          "flex flex-col items-center",
          "p-0.5 rounded-sm",
          "transition-colors duration-200",
          props.showClickMe
            ? CLICK_ME_COLOR
            : props.isSelected
              ? "bg-green-light"
              : "",
        )}
        onMouseDown={(e) => e.preventDefault()}
      >
        {props.showClickMe && (
          <div
            className={cn(
              "absolute",
              props.aspect === Aspect.PORTRAIT
                ? "-top-8.5"
                : "-right-10.5 top-1/2 -translate-y-4",
              props.aspect === Aspect.PORTRAIT
                ? "animate-mild-bounce"
                : "animate-mild-bounce-h",
              CLICK_ME_COLOR,
              "rounded-sm",
              "px-1 pt-0.5 pb-px",
              "text-sm uppercase",
            )}
          >
            Tap
            <div
              className={cn(
                "absolute",
                "h-0 w-0",
                props.aspect === Aspect.PORTRAIT
                  ? "left-1/2 -translate-x-1/2"
                  : "top-1/2 -translate-y-2.25 -left-1",
                props.aspect === Aspect.PORTRAIT
                  ? "border-x-10 border-x-transparent border-t-8 border-t-yellow-300"
                  : "border-y-10 border-y-transparent border-r-8 border-r-yellow-300",
              )}
            />
          </div>
        )}
        <HudButton
          className={cn(
            props.isSelected && !props.disable
              ? BUTTON_SELECTED_CLASS
              : BUTTON_UNSELECTED_CLASS,
            !props.disable && TRANSITION_CLASSES,
          )}
          onClick={props.onClick}
          disabled={props.disable}
        >
          {props.icon}
        </HudButton>
        <p
          className={cn(
            "text-xs uppercase font-sans z-1",
            props.isSelected && !props.disable
              ? LABEL_SELECTED_CLASS
              : LABEL_UNSELECTED_CLASS,
          )}
        >
          {props.labelText}
        </p>
      </div>
    </div>
  );
}
